import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CategoryProgress = ({ data }) => {
  // Enhanced color palette matching your design
  const createGradient = (ctx, chartArea) => {
    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
    gradient.addColorStop(0, 'rgba(107, 115, 255, 0.1)');
    gradient.addColorStop(0.5, 'rgba(107, 115, 255, 0.6)');
    gradient.addColorStop(1, 'rgba(107, 115, 255, 0.9)');
    return gradient;
  };

  const chartData = {
    labels: data.map(item => item._id),
    datasets: [
      {
        label: 'Problems Solved',
        data: data.map(item => item.count),
        backgroundColor: function(context) {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) {
            return null;
          }
          return createGradient(ctx, chartArea);
        },
        borderColor: '#6b73ff',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        hoverBackgroundColor: function(context) {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) {
            return null;
          }
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
          gradient.addColorStop(0, 'rgba(107, 115, 255, 0.2)');
          gradient.addColorStop(0.5, 'rgba(107, 115, 255, 0.7)');
          gradient.addColorStop(1, 'rgba(107, 115, 255, 1)');
          return gradient;
        },
        hoverBorderColor: '#4c46ef',
        hoverBorderWidth: 3,
        // Add shadow effect
        shadowOffsetX: 0,
        shadowOffsetY: 4,
        shadowBlur: 12,
        shadowColor: 'rgba(107, 115, 255, 0.3)',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    animation: {
      duration: 800,
      easing: 'easeOutQuart',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: 'rgba(107, 115, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: false,
        padding: 12,
        titleFont: {
          size: 14,
          weight: '600',
        },
        bodyFont: {
          size: 13,
          weight: '500',
        },
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            return `Problems Solved: ${context.parsed.y}`;
          },
          afterLabel: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed.y / total) * 100).toFixed(1);
            return `${percentage}% of total`;
          },
        },
        external: function(context) {
          // Custom tooltip styling for dark mode
          const tooltip = context.tooltip;
          if (document.documentElement.classList.contains('dark')) {
            context.tooltip.options.backgroundColor = 'rgba(31, 41, 55, 0.95)';
            context.tooltip.options.titleColor = '#f9fafb';
            context.tooltip.options.bodyColor = '#d1d5db';
            context.tooltip.options.borderColor = 'rgba(107, 115, 255, 0.3)';
          }
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#6b7280',
          font: {
            size: 12,
            weight: '500',
          },
          padding: 8,
          callback: function(value) {
            return Number.isInteger(value) ? value : '';
          },
        },
        grid: {
          color: function(context) {
            return document.documentElement.classList.contains('dark') 
              ? 'rgba(75, 85, 99, 0.3)' 
              : 'rgba(229, 231, 235, 0.8)';
          },
          lineWidth: 1,
          drawBorder: false,
        },
        border: {
          display: false,
        },
      },
      x: {
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
            weight: '500',
          },
          maxRotation: 45,
          minRotation: 0,
          padding: 8,
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
    },
    elements: {
      bar: {
        borderRadius: {
          topLeft: 8,
          topRight: 8,
        },
      },
    },
    // Add hover animations
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
  };

  // Calculate stats for the header
  const totalProblems = data.reduce((sum, item) => sum + item.count, 0);
  const topCategory = data.length > 0 ? data.reduce((prev, current) => 
    (prev.count > current.count) ? prev : current
  ) : null;

  return (
    <div className="card">
      {/* Enhanced Header */}
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Category Progress
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Problems solved across different categories
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gradient">
              {totalProblems}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Total Problems
            </div>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="card-body">
        <div className="h-72 relative">
          <Bar data={chartData} options={options} />
          
          {/* Overlay gradient for visual enhancement */}
          <div className="absolute inset-0 pointer-events-none rounded-2xl bg-gradient-to-t from-white/5 to-transparent dark:from-gray-900/10"></div>
        </div>

        {/* Enhanced Stats Footer */}
        {topCategory && (
          <div className="mt-6 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Top Category: <span className="text-primary-600 dark:text-primary-400 font-semibold">
                    {topCategory._id}
                  </span>
                </span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {topCategory.count} problems ({((topCategory.count / totalProblems) * 100).toFixed(1)}%)
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryProgress;